import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import MarkdownView from "@/components/MarkdownView";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Upload, ArrowLeft, ExternalLink, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: "", slug: "", excerpt: "", body: "", cover_image: "", tags: "", seo_title: "", seo_description: "", status: "draft" };

export default function BlogAdmin() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | post-or-EMPTY
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [autoPublish, setAutoPublish] = useState(null); // null until loaded
  const isSuperAdmin = !!user?.is_super_admin;

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/blog/settings").then((r) => setAutoPublish(!!r.data.daily_blog_auto_publish)).catch(() => {});
  }, [isSuperAdmin]);

  const toggleAutoPublish = async (val) => {
    setAutoPublish(val);
    try {
      await api.put("/blog/settings", { daily_blog_auto_publish: val });
      toast.success(val ? "Daily AI posts will now auto-publish" : "Daily AI posts will be saved as drafts");
    } catch (e) { setAutoPublish(!val); toast.error(apiErr(e)); }
  };

  const genDraft = async () => {
    const topic = aiTopic.trim();
    if (!topic) return toast.error("Enter a topic for the AI");
    setAiLoading(true);
    try {
      const { data } = await api.post("/blog/ai-draft", { topic, with_image: true });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        body: data.body || f.body,
        tags: (data.tags || []).join(", "),
        seo_title: data.seo_title || f.seo_title,
        seo_description: data.seo_description || f.seo_description,
        cover_image: data.cover_image || f.cover_image,
      }));
      toast.success("AI draft ready — review, tweak and publish");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAiLoading(false); }
  };

  const load = () => api.get("/blog/posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setForm(EMPTY); setEditing(EMPTY); setPreview(false);
      searchParams.delete("new"); setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setEditing(EMPTY); setPreview(false); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, tags: (p.tags || []).join(", ") });
    setEditing(p); setPreview(false);
  };

  const uploadImage = async (file, intoBody) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (intoBody) setForm((f) => ({ ...f, body: `${f.body}\n\n![image](${data.url})\n` }));
      else setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const save = async (status) => {
    if (!form.title.trim()) return toast.error("Add a title first");
    setSaving(true);
    const payload = {
      title: form.title, slug: form.slug, excerpt: form.excerpt, body: form.body,
      cover_image: form.cover_image, seo_title: form.seo_title, seo_description: form.seo_description,
      status: status || form.status,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing && editing.id) await api.put(`/blog/posts/${editing.id}`, payload);
      else await api.post("/blog/posts", payload);
      toast.success(payload.status === "published" ? "Published!" : "Saved as draft");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/blog/posts/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (editing) {
    const coverSrc = form.cover_image ? (form.cover_image.startsWith("http") ? form.cover_image : BACKEND + form.cover_image) : "";
    return (
      <div className="max-w-3xl mx-auto" data-testid="blog-editor">
        <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5" data-testid="editor-back"><ArrowLeft size={15} /> Back to posts</button>
        <h1 className="font-heading font-extrabold text-2xl mb-6">{editing.id ? "Edit post" : "New post"}</h1>

        <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="ai-draft-box">
          <div className="flex items-center gap-2 mb-2"><Sparkles size={16} className="text-primary" /><p className="font-heading font-semibold text-sm">Draft with AI <span className="text-muted-foreground font-normal">(GPT-5.6 Terra)</span></p></div>
          <p className="text-xs text-muted-foreground mb-3">Give a topic and let AI write a full draft with a cover image. Review before publishing.</p>
          <div className="flex items-center gap-2">
            <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g. 5 ways to boost youth team attendance" data-testid="ai-topic-input" onKeyDown={(e) => { if (e.key === "Enter") genDraft(); }} />
            <Button onClick={genDraft} disabled={aiLoading} className="brand-gradient text-white border-0 shrink-0" data-testid="ai-draft-btn"><Sparkles size={15} className="mr-1.5" /> {aiLoading ? "Writing…" : "Generate"}</Button>
          </div>
        </div>

        <div className="space-y-5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="How to boost attendance at your club" className="mt-1.5" data-testid="editor-title" /></div>
          <div><Label>URL slug <span className="text-muted-foreground font-normal">(optional — auto-generated from title)</span></Label><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="boost-attendance" className="mt-1.5" data-testid="editor-slug" /></div>

          <div>
            <Label>Cover image</Label>
            <div className="flex items-center gap-3 mt-1.5">
              <Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="Paste an image URL or upload" className="flex-1" data-testid="editor-cover-url" />
              <label className="shrink-0">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], false)} data-testid="editor-cover-upload" />
                <span className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-border text-sm cursor-pointer hover:bg-muted"><Upload size={14} /> {uploading ? "…" : "Upload"}</span>
              </label>
            </div>
            {coverSrc && <img src={coverSrc} alt="cover preview" className="mt-3 rounded-xl border border-border aspect-[16/9] object-cover w-full" data-testid="editor-cover-preview" />}
          </div>

          <div><Label>Excerpt / summary</Label><Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="One or two sentences shown on the blog list and in search results." rows={2} className="mt-1.5" data-testid="editor-excerpt" /></div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Body <span className="text-muted-foreground font-normal">(Markdown)</span></Label>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], true)} data-testid="editor-body-image" />
                  <Upload size={13} /> Insert image
                </label>
                <button onClick={() => setPreview((v) => !v)} className="inline-flex items-center gap-1 text-xs text-primary" data-testid="editor-preview-toggle"><Eye size={13} /> {preview ? "Edit" : "Preview"}</button>
              </div>
            </div>
            {preview ? (
              <div className="mt-1.5 rounded-md border border-border p-4 min-h-[300px]" data-testid="editor-preview"><MarkdownView>{form.body}</MarkdownView></div>
            ) : (
              <Textarea value={form.body} onChange={(e) => set("body", e.target.value)} placeholder={"## Heading\n\nWrite your post using **Markdown** — headings, **bold**, [links](https://example.com), lists and images."} rows={16} className="mt-1.5 font-mono text-sm" data-testid="editor-body" />
            )}
          </div>

          <div><Label>Tags <span className="text-muted-foreground font-normal">(comma-separated)</span></Label><Input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Coaching, Attendance, Payments" className="mt-1.5" data-testid="editor-tags" /></div>

          <div className="rounded-xl border border-border p-4 space-y-4">
            <p className="font-heading font-semibold text-sm">Search engine (SEO)</p>
            <div><Label>SEO title <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="Defaults to the post title" className="mt-1.5" data-testid="editor-seo-title" /></div>
            <div><Label>Meta description <span className="text-muted-foreground font-normal">(optional)</span></Label><Textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} placeholder="~155 characters that appear under the title on Google. Defaults to the excerpt." rows={2} className="mt-1.5" data-testid="editor-seo-desc" /></div>
          </div>

          <div className="flex items-center gap-3 pt-2 pb-10">
            <Button onClick={() => save("published")} disabled={saving} className="brand-gradient text-white border-0" data-testid="editor-publish">{saving ? "Saving…" : "Publish"}</Button>
            <Button onClick={() => save("draft")} disabled={saving} variant="outline" data-testid="editor-save-draft">Save draft</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="blog-admin">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold text-2xl">Blog</h1>
          <p className="text-sm text-muted-foreground mt-1">Write posts that bring your club to the top of Google.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm"><a href="/blog" target="_blank" rel="noopener noreferrer" data-testid="blog-view-public"><ExternalLink size={14} className="mr-1.5" /> View blog</a></Button>
          <Button onClick={openNew} className="brand-gradient text-white border-0" data-testid="blog-new-post"><Plus size={16} className="mr-1.5" /> New post</Button>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4 flex items-start justify-between gap-4" data-testid="auto-publish-card">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-primary" />
              <p className="font-heading font-semibold text-sm">Auto-publish daily AI posts</p>
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">When on, the two blog posts generated automatically each day go live instead of waiting as drafts for review.</p>
          </div>
          <Switch checked={!!autoPublish} disabled={autoPublish === null} onCheckedChange={toggleAutoPublish} data-testid="auto-publish-toggle" />
        </div>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl" data-testid="blog-admin-empty">
          <p className="font-heading font-semibold text-lg">No posts yet</p>
          <p className="text-muted-foreground text-sm mt-1 mb-5">Publish your first article to start ranking on search engines.</p>
          <Button onClick={openNew} className="brand-gradient text-white border-0"><Plus size={16} className="mr-1.5" /> Write your first post</Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="blog-admin-list">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4" data-testid={`blog-row-${p.id}`}>
              <div className="w-16 h-16 rounded-lg bg-muted overflow-hidden shrink-0">
                {p.cover_image ? <img src={p.cover_image.startsWith("http") ? p.cover_image : BACKEND + p.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full brand-gradient opacity-70" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${p.status === "published" ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"}`} data-testid={`blog-status-${p.id}`}>{p.status}</span>
                  {p.views > 0 && <span className="text-[11px] text-muted-foreground">{p.views} views</span>}
                </div>
                <p className="font-medium truncate mt-1">{p.title}</p>
                <p className="text-xs text-muted-foreground truncate">/blog/{p.slug}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {p.status === "published" && <Button asChild size="icon" variant="ghost"><a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`blog-open-${p.id}`}><Eye size={16} /></a></Button>}
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`}><Pencil size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-destructive hover:text-destructive" data-testid={`blog-delete-${p.id}`}><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
